import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageSquareText, Video, Mail, AlertTriangle, ShieldCheck, CheckCircle2, ArrowLeft, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import DemoBanner from "./DemoBanner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const TENANT = "company-a";

function buildTelHref(num) { return num ? `tel:${num.replace(/[^0-9+]/g, "")}` : null; }
function buildSmsHref(num, prefill) {
  if (!num) return null;
  const n = num.replace(/[^0-9+]/g, "");
  const q = prefill ? `?body=${encodeURIComponent(prefill)}` : "";
  return `sms:${n}${q}`;
}

export default function CompanyAEmployeeDemo() {
  const [settings, setSettings] = useState({ phone_number: "", sms_number: "", video_link: "", sms_prefill: "" });
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = "Company A — Live Employee Demo";
    axios.get(`${API}/demo/${TENANT}/settings`)
      .then((r) => setSettings(r.data))
      .catch(() => {});
  }, []);

  const cards = [
    { key: "call", icon: Phone, title: "Call a Chaplain", copy: "See how an employee would connect by phone with the configured CALLACHAP care team.", cta: "Demonstrate Call" },
    { key: "text", icon: MessageSquareText, title: "Text a Chaplain", copy: "See how an employee would reach out by text using the configured organization-specific channel.", cta: "Demonstrate Text" },
    { key: "video", icon: Video, title: "Remote Support", copy: "See how additional remote-support options may be requested or scheduled when included in the care arrangement.", cta: "Demonstrate Remote Support" },
    { key: "message", icon: Mail, title: "Send a Confidential Message", copy: "Submit a demonstration support message and see how the workflow is reflected in the isolated Company A demo environment.", cta: "Send Demo Message" },
  ];

  const telHref = buildTelHref(settings.phone_number);
  const smsHref = buildSmsHref(settings.sms_number || settings.phone_number, settings.sms_prefill);
  const videoLink = settings.video_link;

  return (
    <main data-testid="company-a-employee-demo" className="bg-white text-slate-900 min-h-screen">
      <DemoBanner title="Company A — CCCHAPS Care Portal Demonstration Environment" subtitle="Functional demonstration — activity is isolated from live records." />
      <section className="pt-8 md:pt-12 pb-6 md:pb-8 border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/care-portal/demo/company-a" data-testid="ca-emp-back" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.2} /> Back to Company A
          </Link>

          {/* Identity */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold" data-testid="ca-emp-eyebrow">
                  Company A · Functional Demonstration
                </p>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-800">
                  <Sparkles className="w-3 h-3" strokeWidth={2.4} /> Live Demo
                </span>
              </div>
              <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold" data-testid="ca-emp-provider-label">
                CALLACHAP · Provided by CCCHAPS
              </p>
              <h1 className="mt-1 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900" data-testid="ca-emp-heading">
                Experience CALLACHAP
              </h1>
              <p className="mt-3 text-base text-slate-600 max-w-2xl leading-relaxed" data-testid="ca-emp-intent-line">
                Choose a support option below to see how an employee would connect through an organization-specific CALLACHAP care arrangement.
              </p>
            </div>
            <div className="shrink-0">
              <img src="/callachap-logo.png" alt="CALLACHAP" className="h-9 md:h-11 w-auto" />
            </div>
          </div>

          {/* Functional Demonstration note */}
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 max-w-3xl" data-testid="ca-emp-functional-note">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" strokeWidth={2.2} aria-hidden="true" />
              <p className="text-sm text-slate-800 font-medium">Functional Demonstration</p>
            </div>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed pl-6">
              Actions taken here are used only within the isolated Company A demonstration environment so prospective organizations can see how the CALLACHAP workflow operates. Demo submissions may be stored inside the isolated Company A demonstration environment (auto-removed after 30 days; not production employee data).
            </p>
          </div>

          {/* Privacy reassurance */}
          <div className="mt-3 rounded-xl border border-slate-200 bg-[#F0F4F8]/60 px-4 py-3 max-w-3xl" data-testid="ca-emp-privacy-block">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-[#4A6FA5] shrink-0" strokeWidth={2.2} aria-hidden="true" />
              <p className="text-sm text-slate-800 font-medium">What stays private</p>
            </div>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed pl-6">
              The employer does not receive the private details of an employee's chaplain conversation. Organizations may receive aggregate, non-identifying utilization information and operational reporting consistent with the care arrangement and privacy safeguards.
            </p>
          </div>

          {/* 24/7 note */}
          <div className="mt-3 max-w-3xl text-xs text-slate-500 leading-relaxed" data-testid="ca-emp-247-note">
            24/7 on-call chaplain access is available through participating organization care arrangements. Response times may vary by channel.
          </div>
        </div>
      </section>

      <section className="pt-8 md:pt-10 pb-6 md:pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {!active && (
            <div data-testid="ca-emp-method-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
              {cards.map((c) => (
                <button key={c.key} type="button" onClick={() => setActive(c.key)} data-testid={`ca-emp-method-${c.key}`}
                  className="group flex flex-col items-start gap-3 p-5 md:p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#4A6FA5] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-left h-full">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#F0F4F8] text-[#4A6FA5] group-hover:bg-[#4A6FA5] group-hover:text-white transition-colors">
                    <c.icon className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} aria-hidden="true" />
                  </span>
                  <span className="font-heading text-lg md:text-xl text-slate-900 tracking-tight leading-snug">{c.title}</span>
                  <span className="text-xs md:text-sm text-slate-600 leading-relaxed">{c.copy}</span>
                  <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#4A6FA5] group-hover:text-[#3B597B] uppercase tracking-wider">
                    {c.cta} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
                  </span>
                </button>
              ))}
            </div>
          )}

          {active === "call" && (
            <ActionPanel title="Call a chaplain" onBack={() => setActive(null)}>
              {telHref ? (
                <>
                  <p className="text-sm text-slate-600">Your phone will dial the CALLACHAP demo line.</p>
                  <a href={telHref} data-testid="ca-emp-tel-link" className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider w-full sm:w-auto">
                    <Phone className="w-4 h-4" strokeWidth={2.2} /> Start Call ({settings.phone_number})
                  </a>
                </>
              ) : <NotConfigured what="phone number" />}
            </ActionPanel>
          )}

          {active === "text" && (
            <ActionPanel title="Text a chaplain" onBack={() => setActive(null)}>
              {smsHref ? (
                <>
                  <p className="text-sm text-slate-600">Opens your native SMS composer with a starter message.</p>
                  <a href={smsHref} data-testid="ca-emp-sms-link" className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider w-full sm:w-auto">
                    <MessageSquareText className="w-4 h-4" strokeWidth={2.2} /> Start Text ({settings.sms_number || settings.phone_number})
                  </a>
                </>
              ) : <NotConfigured what="SMS number" />}
            </ActionPanel>
          )}

          {active === "video" && (
            <ActionPanel title="Remote support" onBack={() => setActive(null)}>
              {videoLink ? (
                <>
                  <p className="text-sm text-slate-600">Opens the CALLACHAP demo meeting in a new tab.</p>
                  <a href={videoLink} target="_blank" rel="noopener noreferrer" data-testid="ca-emp-video-link"
                    className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider w-full sm:w-auto">
                    <Video className="w-4 h-4" strokeWidth={2.2} /> Join Meeting <ExternalLink className="w-4 h-4" />
                  </a>
                </>
              ) : <p className="text-sm text-slate-600">Live meeting link not configured yet. Use the fallback below.</p>}

              <VideoRequestForm />
            </ActionPanel>
          )}

          {active === "message" && (
            <ActionPanel title="Send a confidential message" onBack={() => setActive(null)}>
              <MessageForm contactType="message" />
            </ActionPanel>
          )}
        </div>
      </section>

      <section className="pb-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          {!active && (
            <Link
              to="/care-portal/demo/company-a/employer"
              data-testid="ca-emp-to-employer"
              className="flex items-center justify-between gap-4 p-5 rounded-xl bg-white border border-slate-200 hover:border-[#4A6FA5] hover:shadow-md transition-all"
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">Next in the demo</p>
                <p className="mt-1 font-heading text-lg text-slate-900 tracking-tight">See What the Organization Sees</p>
                <p className="mt-1 text-xs text-slate-600">Open the Company A employer dashboard to view aggregate, non-identifying utilization.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#4A6FA5] shrink-0" strokeWidth={2.2} />
            </Link>
          )}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FFF8ED] border border-[#F5D68A]/60">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-[#B8791A]" strokeWidth={2} />
            <p className="text-sm text-[#7A4F0F] leading-relaxed">
              CALLACHAP is not an emergency, medical, or psychiatric service. If you are experiencing a life-threatening emergency, call 911.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

function NotConfigured({ what }) {
  return <p className="text-sm text-slate-500">Demo {what} not configured yet. An authorized admin can set it at Admin → Demo Settings.</p>;
}

function ActionPanel({ title, children, onBack }) {
  return (
    <div className="rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm space-y-4">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="ca-emp-panel-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={2.2} /> Choose a different method
      </button>
      <h3 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">{title}</h3>
      {children}
    </div>
  );
}

function VideoRequestForm() {
  const [form, setForm] = useState({ first_name: "", email: "", best_time_to_contact: "", reason: "Video conversation requested." });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  if (done) return <div data-testid="ca-emp-video-done" className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">Demo request received. A chaplain would follow up with a scheduled video conversation.</div>;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.reason) { toast.error("Name, email, and note are required."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/demo/${TENANT}/support-requests`, { ...form, preferred_contact: "video", contact_type: "video_request", consent: true });
      toast.success("Video request received (demo)");
      setDone(true);
    } catch (err) { toast.error("Failed to submit demo request"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} data-testid="ca-emp-video-form" className="mt-6 space-y-3 pt-4 border-t border-slate-100">
      <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">Fallback: request a remote-support conversation</p>
      <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="ca-emp-video-name" />
      <input type="email" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ca-emp-video-email" />
      <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Best time to reach you" value={form.best_time_to_contact} onChange={(e) => setForm({ ...form, best_time_to_contact: e.target.value })} data-testid="ca-emp-video-time" />
      <button type="submit" disabled={saving} data-testid="ca-emp-video-submit" className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider">
        {saving ? "Sending…" : "Request Video Conversation"}
      </button>
    </form>
  );
}

function MessageForm({ contactType = "message" }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", preferred_contact: "email", best_time_to_contact: "", reason: "", urgency: "routine" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  if (done) return (
    <div data-testid="ca-emp-message-done" className="p-5 rounded-xl bg-emerald-50 border border-emerald-200">
      <div className="flex items-center gap-2 text-emerald-800 font-semibold"><CheckCircle2 className="w-5 h-5" strokeWidth={2.2} /> Demo request received</div>
      <p className="mt-2 text-sm text-emerald-800">Your Company A demo message has been recorded in the CCCHAPS admin demo view.</p>
    </div>
  );
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.reason) { toast.error("Name, email, and message are required."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/demo/${TENANT}/support-requests`, { ...form, contact_type: contactType, consent: true });
      toast.success("Demo message submitted");
      setDone(true);
    } catch (err) { toast.error("Failed to submit demo message"); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} data-testid="ca-emp-message-form" className="space-y-3">
      <div className="flex items-start gap-2.5 p-3 rounded-lg bg-[#F0F4F8] border border-[#4A6FA5]/15 text-sm text-[#3B597B]">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
        <p>This demonstration environment is completely separate from the live CCCHAPS Care Portal. All demonstration activity is isolated from actual employee support records and production reporting. Demo information is automatically removed after 30 days and may also be cleared after a presentation.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="First name*" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="ca-emp-message-firstname" />
        <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="ca-emp-message-lastname" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input type="email" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Email*" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ca-emp-message-email" />
        <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ca-emp-message-phone" />
      </div>
      <textarea rows={4} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none" placeholder="Brief support request*" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="ca-emp-message-reason" />
      <button type="submit" disabled={saving} data-testid="ca-emp-message-submit" className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider">
        {saving ? "Sending…" : "Send Confidential Demo Message"} <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
      </button>
    </form>
  );
}
